import { ConversationParticipantEntity } from '@provision/conversation-types';
import { useCallback, useMemo } from 'react';
import { Address, Conversation, useConversationParticipants } from 'src/graphql';
import { useConversationParticipationRecords } from 'src/graphql/entities/conversation-participation/hooks/use-conversation-participation-records';
import { DEFAULT_PAGINATION_LIMIT, useSearch } from 'src/graphql/entities/search/use-search';
import { ConditionInput, EntityNames } from 'src/graphql/types';

export const useConversationAssociatedSites = (
  conversation?: Conversation,
  input?: string,
  includeDeleted?: boolean,
) => {
  const initialRequester = conversation?.requesterData;

  const { conversationParticipations: participations } = useConversationParticipationRecords({
    conversationId: conversation?.conversationId || '',
    skip: !!initialRequester || !!conversation?.associatedAddresses.length,
    includeDeleted,
  });

  const queryParams = useMemo(() => {
    return conversation
      ? {
          participantIds: participations?.map(
            (participation) => participation.channel.conversationParticipantId,
          ),
          filter: [
            {
              attribute: 'participantEntity',
              operator: '=',
              value: ConversationParticipantEntity.Client,
            },
          ],
          includeDeleted,
        }
      : {};
  }, [conversation, participations, includeDeleted]);

  const { conversationParticipants, loading: conversationParticipantsLoading } =
    useConversationParticipants(queryParams, {
      skip: !conversation || !participations?.length,
    });

  const clientIds = useMemo(
    () =>
      initialRequester
        ? [initialRequester.participantIdentity]
        : conversationParticipants?.map((participant) => participant.participantIdentity),
    [conversationParticipants, initialRequester],
  );

  const filters = useMemo<ConditionInput[]>(
    () => [
      {
        attribute: 'clients/clientId',
        operator: 'in',
        value: Array.isArray(clientIds) ? clientIds.toSorted().join(',') : clientIds,
      },
    ],
    [clientIds],
  );

  const {
    records: addresses,
    loadMore,
    hasMore,
    isLoading: loading,
    isLoadingMore,
    cacheManager,
  } = useSearch({
    skip: !conversation,
    entityName: EntityNames.Site,
    searchTerm: input ?? null,
    showResultsWhenEmptySearchTerm: true,
    filter: filters,
    limit: DEFAULT_PAGINATION_LIMIT * 3,
  }) ?? {};

  const clientAddresses = useMemo(
    () => (addresses ? Object.values(addresses) : []),
    [addresses],
  );

  const updateAssociatedSitesCache = useCallback(
    (newValue: Address) => {
      cacheManager?.update?.((cache) => {
        cache?.getAddress.records.push(newValue as (typeof cache.getAddress.records)[0]);
      });
    },
    [cacheManager],
  );

  return useMemo(
    () => ({
      addresses: clientAddresses,
      loading: conversationParticipantsLoading || loading,
      hasMore,
      loadMore,
      isLoadingMore,
      updateAssociatedSitesCache,
      filters,
    }),
    [
      clientAddresses,
      conversationParticipantsLoading,
      filters,
      hasMore,
      loadMore,
      loading,
      isLoadingMore,
      updateAssociatedSitesCache,
    ],
  );
};
